/* AI Reply tab — extracted from FormBuilder.jsx (A7a) */
import React, { useRef } from 'react';
import { doc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { resolveFromName, domainSenderName, GOCTA_OWNED_WORKSPACE_ID } from '../../utils/replyIdentity';
import LogoUrlUploadField from '../LogoUrlUploadField';
import SenderPicker from '../SenderPicker';
import TestAIConversation from './TestAIConversation';
import { signatureDisagreesWithFromName, SIGNATURE_MISMATCH_COPY } from '../../utils/signatureMismatch';
import { buildConsentText, DEFAULT_SMS_CONSENT_TEXT } from '../../utils/smsConsent';

export default function AiReplyTab({
  canAccessAIReplyTab,
  formSettings,
  setFormSettings,
  formName,
  formId,
  form,
  currentUser,
  selectedWorkspaceId,
  workspaceBrand,
  senderMode,
  setSenderMode,
  availableDomains,
  setAvailableDomains,
  selectedDomainId,
  setSelectedDomainId,
  customDomain: _customDomain,
  setCustomDomain,
  customDomainFromEmail: _customDomainFromEmail,
  setCustomDomainFromEmail,
  customDomainStatus: _customDomainStatus,
  setCustomDomainStatus,
  customDomainDnsRecords: _customDomainDnsRecords,
  setCustomDomainDnsRecords,
  aiCalendarLink,
  setAiCalendarLink,
  aiReplyEnabled,
  setAiReplyEnabled,
  testLeadName,
  setTestLeadName,
  testLeadEmail,
  setTestLeadEmail,
  testLeadMessage,
  setTestLeadMessage,
  testingAiReply,
  aiReplyPreview,
  setAiReplyPreview,
  conversationHistory,
  setConversationHistory,
  sendingTestEmail,
  testEmailRecipient,
  setTestEmailRecipient,
  testEmailName,
  setTestEmailName,
  testEmailMessage,
  setTestEmailMessage,
  showToast,
  navigate,
  onOpenKnowledgeTab,
  testAIReply,
  sendTestEmail,
  sendRealTestEmail,
  showSmsOptInBuilder = false,
  fields = [],
  onAddPhoneField = () => {},
  workspaceSmsA2pApproved = false,
}) {
  const initialSmsConsentRef = useRef(formSettings.smsOptIn?.consentText || '');
  const smsConsentEdited =
    workspaceSmsA2pApproved &&
    !!formSettings.smsOptIn?.enabled &&
    (formSettings.smsOptIn?.consentText || '') !== initialSmsConsentRef.current;

  function applyDomainSelection(domain) {
    if (!domain) {
      setSenderMode('gocta');
      return;
    }
    setSenderMode('domain');
    setSelectedDomainId(domain.id);
    setCustomDomain(domain.domain);
    setCustomDomainFromEmail((domain.fromEmail || '').split('@')[0] || 'replies');
    setCustomDomainStatus({
      verified: domain.verificationStatus === 'verified',
      status: domain.verificationStatus,
    });
    setCustomDomainDnsRecords(domain.dnsRecords);
  }

  function handleSenderAdded(result) {
    const added = result.domain;
    if (added?.id && setAvailableDomains) {
      setAvailableDomains((prev) => [
        added,
        ...prev.filter((d) => d.id !== added.id && d.fromEmail !== added.fromEmail),
      ]);
    }
    if (added) applyDomainSelection(added);
  }

  return (
          <div>
            {!canAccessAIReplyTab ? (
              <div className="fb-card" style={{ textAlign: 'center' }}>
                <h3 className="fb-subsection-title" style={{ marginTop: 0, color: 'var(--danger)' }}>Access Denied</h3>
                <p className="fb-muted">
                  You don't have permission to access AI Reply settings. Contact your workspace owner to request access.
                </p>
              </div>
            ) : (
              <div>
            {/* Knowledge base status */}
            <div className="fb-card" style={{ marginBottom: '2rem', padding: '1rem 1.5rem' }}>
              <p style={{ margin: 0, fontSize: '0.95rem' }}>
                Knowledge base:{' '}
                {formSettings.knowledgeBaseId ? (
                  <strong>linked ✓</strong>
                ) : (
                  <>
                    not set —{' '}
                    <button
                      type="button"
                      onClick={onOpenKnowledgeTab}
                      className="fb-link-inline today-link-accent"
                    >
                      set it up in the Knowledge tab
                    </button>
                  </>
                )}
              </p>
            </div>

            <h2 className="fb-section-title">1. Identity & Signature</h2>
            {/* Email Configuration */}
            <div className="fb-card" style={{ marginBottom: '2rem' }}>
              <h3 className="fb-subsection-title" style={{ marginTop: 0 }}>Email Configuration</h3>
              <p className="fb-muted" style={{ marginBottom: '1.5rem' }}>
                Choose where AI replies will be sent from:
              </p>

              {/* Reply identity — "Shown as" name. Never form.name, never a "gocta" string —
                  see src/utils/replyIdentity.js (mirrors functions-forms/src/ai-reply/replyIdentity.ts) */}
              {(() => {
                const selectedDomainObj = availableDomains.find(d => d.id === selectedDomainId);
                const domainVerified = selectedDomainObj?.verificationStatus === 'verified';
                const usingDomain = senderMode === 'domain' && !!selectedDomainObj && domainVerified;
                const activeFromEmail = usingDomain ? selectedDomainObj.fromEmail : 'replies@gocta.ai';
                const isGoctaOwnedWorkspace = selectedWorkspaceId === GOCTA_OWNED_WORKSPACE_ID;
                const allowPlatformBrand = isGoctaOwnedWorkspace;
                const activeFromName = resolveFromName({
                  formBusinessName: formSettings.businessName,
                  workspaceBusinessName: workspaceBrand.businessName,
                  workspaceName: workspaceBrand.name,
                  domainFromName: usingDomain ? domainSenderName(selectedDomainObj) : undefined,
                  allowPlatformBrand,
                });
                const shownAsContainsGocta = !isGoctaOwnedWorkspace && /gocta/i.test(formSettings.businessName || '');

                let maskingStatus;
                if (usingDomain && selectedDomainObj.inboundEnabled) {
                  maskingStatus = { icon: '✅', tone: 'success', text: 'Fully branded — From and replies both use your domain.' };
                } else if (usingDomain) {
                  maskingStatus = { icon: '⚠️', tone: 'warn', text: 'From is your domain; replies route via gocta.ai — enable inbound on the Custom Domains page to fully mask.' };
                } else if (senderMode === 'domain' && selectedDomainObj && !domainVerified) {
                  maskingStatus = { icon: '⏳', tone: 'warn', text: `${selectedDomainObj.domain} is pending DNS verification — until verified, replies send from replies@gocta.ai (with your brand name shown).` };
                } else {
                  maskingStatus = { icon: '⚠️', tone: 'warn', text: 'Brand name shown, but the address is replies@gocta.ai. Add and verify your domain to fully mask.' };
                }

                return (
                  <>
                    <div style={{ marginBottom: '1.5rem' }}>
                      <label className="fb-label">
                        Shown as (From name)
                      </label>
                      <input
                        type="text"
                        className={`fb-input fb-input-wide${shownAsContainsGocta ? ' fb-input-error' : ''}`}
                        value={formSettings.businessName}
                        onChange={(e) => setFormSettings({ ...formSettings, businessName: e.target.value })}
                        placeholder={workspaceBrand.businessName || workspaceBrand.name || 'Your Business Name'}
                      />
                      <p className="fb-muted" style={{ margin: '0.5rem 0 0 0', fontSize: '0.85rem' }}>
                        Recipients see this name on every reply. Defaults to your workspace name — never the internal form name, never "gocta.ai".
                      </p>
                      {shownAsContainsGocta && (
                        <p className="fb-inline-error" style={{ margin: '0.35rem 0 0 0' }}>
                          ⚠️ "gocta" can't be used as a From name — this will be ignored and your workspace name will show instead.
                        </p>
                      )}
                    </div>

                    <SenderPicker
                      mode="picker"
                      workspaceId={selectedWorkspaceId}
                      senders={availableDomains}
                      selectedSenderId={selectedDomainId}
                      senderMode={senderMode}
                      showToast={showToast}
                      onSelect={({ mode: nextMode, sender }) => {
                        if (nextMode === 'gocta') {
                          setSenderMode('gocta');
                          return;
                        }
                        applyDomainSelection(sender);
                      }}
                      onAdded={handleSenderAdded}
                      onCheckStatus={async (domainId, fromEmail, result) => {
                        if (!result) return;
                        const verified = result.verified === true;
                        setAvailableDomains?.((prev) => prev.map((d) => (
                          d.id === domainId
                            ? {
                              ...d,
                              verificationStatus: verified ? 'verified' : 'pending',
                              waitingOn: result.waitingOn || d.waitingOn,
                            }
                            : d
                        )));
                        if (selectedDomainId === domainId) {
                          setCustomDomainStatus({
                            verified,
                            status: verified ? 'verified' : 'pending',
                          });
                        }
                      }}
                    />

                    {/* Live preview — always visible, computed the same way replyQueueProcessor
                        resolves the actual send (src/utils/replyIdentity.js mirrors the backend). */}
                    <div className="fb-preview-from">
                      <div className="fb-preview-from-label">Recipients see:</div>
                      <div className="fb-preview-from-value">
                        "{activeFromName}" &lt;{activeFromEmail}&gt;
                      </div>
                      <div className={`fb-callout ${maskingStatus.tone === 'success' ? 'success' : 'warn'}`} style={{ marginTop: '0.75rem', fontSize: '0.85rem' }}>
                        {maskingStatus.icon} {maskingStatus.text}
                      </div>
                    </div>
                  </>
                );
              })()}

            </div>

            <div className="fb-card" style={{ marginBottom: '2rem' }}>
              {/* Email Plus Address (for form identification) */}
              {formSettings.knowledgeBaseId && (
                <div className="fb-subcard" style={{ marginTop: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                    Email Identifier (Optional)
                  </label>
                  <p style={{ fontSize: '0.85rem', marginBottom: '1rem', opacity: 0.9 }}>
                    Customize the email address used for replies. Keep it short and simple!
                    <br />
                    <strong>Example:</strong> replies+<span style={{ color: '#2ecc71' }}>brand-a3f9k2</span>@gocta.ai
                    <br />
                    <span style={{ fontSize: '0.75rem', color: '#666' }}>
                      Use any name you want! The short code ensures uniqueness. Letters and numbers only.
                    </span>
                  </p>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <input
                      type="text"
                      placeholder="brand"
                      value={formSettings.emailPlusAddress || ''}
                      onChange={(e) => {
                        const newSettings = { ...formSettings, emailPlusAddress: e.target.value };
                        setFormSettings(newSettings);
                      }}
                      style={{
                        flex: 1,
                        padding: '0.75rem',
                        border: '2px solid #e0e0e0',
                        borderRadius: '8px',
                        fontSize: '0.9rem',
                        background: 'white',
                        color: '#333',
                      }}
                    />
                    <button
                      onClick={async () => {
                        if (!formId || !currentUser) {
                          showToast('Please save the form first', 'info');
                          return;
                        }
                        
                        const customId = formSettings.emailPlusAddress?.trim();
                        if (!customId) {
                          // Allow clearing it
                          try {
                            await updateDoc(doc(db, 'forms', formId), {
                              'settings.emailPlusAddress': null,
                            });
                            showToast('Email identifier cleared!');
                            return;
                          } catch (error) {
                            console.error('Error saving:', error);
                            showToast('Error saving. Check console for details.', 'error');
                            return;
                          }
                        }
                        
                        // Clean the identifier
                        const cleanId = customId.toLowerCase().replace(/[^a-z0-9]/g, '');
                        
                        // Check if user has custom domain (only validate duplicates for custom domain users)
                        try {
                          const domainsRef = collection(db, 'email_domains');
                          const domainQuery = query(
                            domainsRef,
                            where('workspaceId', '==', selectedWorkspaceId || form?.workspaceId || currentUser.uid),
                            where('verificationStatus', '==', 'verified')
                          );
                          const domainSnapshot = await getDocs(domainQuery);
                          const hasCustomDomain = !domainSnapshot.empty;
                          
                          // Only validate duplicates for custom domain users
                          // Default domain users don't need validation - short code ensures uniqueness
                          if (hasCustomDomain) {
                            // Custom domain users: check for duplicates in workspace
                            const formsRef = collection(db, 'forms');
                            const q = query(
                              formsRef,
                              where('workspaceId', '==', selectedWorkspaceId || form?.workspaceId || currentUser.uid),
                              where('settings.emailPlusAddress', '==', cleanId)
                            );
                            const snapshot = await getDocs(q);
                            const existing = snapshot.docs.find(doc => doc.id !== formId);
                            
                            if (existing) {
                              showToast(`"${cleanId}" already exists in your workspace. Choose another name.`, 'error');
                              return;
                            }
                          }
                          
                          // Save the cleaned version
                          await updateDoc(doc(db, 'forms', formId), {
                            'settings.emailPlusAddress': cleanId,
                          });
                          
                          // Update local state with cleaned version
                          setFormSettings({ ...formSettings, emailPlusAddress: cleanId });
                          
                          showToast('Email identifier saved!');
                        } catch (error) {
                          console.error('Error saving:', error);
                          if (error?.message?.includes('index')) {
                            showToast('Index not ready. Please try again in a moment.', 'info');
                          } else {
                            showToast('Error saving. Check console for details.', 'error');
                          }
                        }
                      }}
                      style={{
                        padding: '0.75rem 1.5rem',
                        background: '#2ecc71',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Save
                    </button>
                  </div>
                  <p style={{ fontSize: '0.75rem', marginTop: '0.5rem', opacity: '0.7' }}>
                    {(() => {
                      const cleanCustom = formSettings.emailPlusAddress 
                        ? formSettings.emailPlusAddress.toLowerCase().replace(/[^a-z0-9]/g, '')
                        : '';
                      // Generate short code preview (first 6 chars of formId hash)
                      const shortCode = formId ? (() => {
                        let hash = 0;
                        for (let i = 0; i < formId.length; i++) {
                          hash = ((hash << 5) - hash) + formId.charCodeAt(i);
                          hash = hash & hash;
                        }
                        return Math.abs(hash).toString(36).substring(0, 6).padStart(6, '0');
                      })() : 'xxxxxx';
                      
                      const preview = cleanCustom 
                        ? `replies+${cleanCustom}-${shortCode}@gocta.ai`
                        : `replies+${shortCode}@gocta.ai`;
                      
                      return (
                        <>
                          Reply-To: <strong>{preview}</strong>
                          <br />
                          <span style={{ fontSize: '0.7rem', color: '#666' }}>
                            The short code ({shortCode}) ensures uniqueness across all users
                          </span>
                        </>
                      );
                    })()}
                  </p>
                </div>
              )}

            </div>
            <div className="fb-card fb-card-accent" style={{ marginBottom: '2rem' }}>
              <h3 className="fb-subsection-title" style={{ marginTop: 0 }}>Email Signature & Branding</h3>
              <p className="fb-muted" style={{ marginBottom: '1.5rem', fontSize: '0.9rem' }}>
                Customize how AI emails are signed. Add your logo and signature text to make emails look professional and branded.
              </p>

              {/* Signature Text */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label className="fb-label">
                  📝 Signature Text
                </label>
                <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.5rem' }}>
                  Customize how AI emails are signed. Press Enter to start a new line.
                  <br />
                  <strong>Example:</strong><br />
                  Best regards,<br />
                  John Doe<br />
                  CEO, Company Name
                </p>
                <textarea
                  value={formSettings.customSignature || ''}
                  onChange={(e) => {
                    setFormSettings({ ...formSettings, customSignature: e.target.value });
                  }}
                  placeholder="Best regards,&#10;John Doe&#10;CEO, Company Name"
                  rows={4}
                  className="fb-textarea" style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                />
                {signatureDisagreesWithFromName(
                  resolveFromName({
                    formBusinessName: formSettings.businessName,
                    workspaceBusinessName: workspaceBrand.businessName,
                    workspaceName: workspaceBrand.name,
                    domainFromName: senderMode === 'domain'
                      ? domainSenderName(availableDomains.find((d) => d.id === selectedDomainId))
                      : undefined,
                  }),
                  formSettings.customSignature
                ) && (
                  <div className="fb-callout warn" data-testid="signature-mismatch" style={{ marginTop: '0.75rem' }}>
                    {SIGNATURE_MISMATCH_COPY}
                  </div>
                )}
              </div>
              <div style={{ marginBottom: '1.5rem' }}>
                <label className="fb-label">
                  🔗 Signature Link (Optional)
                </label>
                <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.5rem' }}>
                  Add a link to your website or social media that will appear in the signature.
                </p>
                <input
                  type="url"
                  value={formSettings.signatureLinkUrl || ''}
                  onChange={(e) => {
                    setFormSettings({ ...formSettings, signatureLinkUrl: e.target.value });
                  }}
                  placeholder="https://yourdomain.com"
                  className="fb-input"
                />
                {formSettings.signatureLinkUrl && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <input
                      type="text"
                      value={formSettings.signatureLinkText || ''}
                      onChange={(e) => {
                        setFormSettings({ ...formSettings, signatureLinkText: e.target.value });
                      }}
                      placeholder="Link text (e.g., 'Visit our website')"
                      className="fb-input" style={{ marginTop: '0.5rem', fontSize: '0.85rem' }}
                    />
                  </div>
                )}
              </div>

              {/* Logo URL */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label className="fb-label">
                  Logo (Optional)
                </label>
                <LogoUrlUploadField
                  value={formSettings.signatureLogoUrl || ''}
                  onChange={(url) => {
                    setFormSettings({ ...formSettings, signatureLogoUrl: url });
                  }}
                  uploadType="form"
                  uploadIdValue={formId}
                  helperText="Upload a logo (we convert it to an email-safe format automatically) or paste a public image URL. Recommended size: 200x50px or similar."
                />
              </div>

              {/* Preview */}
              {(formSettings.customSignature || formSettings.signatureLogoUrl || formSettings.signatureLinkUrl) && (
                <div className="fb-callout tip" style={{ marginBottom: '1.5rem' }}>
                  <strong>Preview:</strong>
                  <div style={{ marginTop: '0.5rem', whiteSpace: 'pre-wrap', fontSize: '0.85rem', color: '#333' }}>
                    {formSettings.customSignature || 'Best regards,\n' + (formName || 'Your Business') + ' Team'}
                    {formSettings.signatureLinkUrl && (
                      <div style={{ marginTop: '0.5rem' }}>
                        {formSettings.signatureLinkText || formSettings.signatureLinkUrl}
                      </div>
                    )}
                    {formSettings.signatureLogoUrl && (
                      <div style={{ marginTop: '0.5rem' }}>
                        <img src={formSettings.signatureLogoUrl} alt="Logo" style={{ maxHeight: '40px', maxWidth: '150px' }} />
                      </div>
                    )}
                  </div>
                </div>
              )}

            </div>

            <h2 className="fb-section-title">2. Cadence</h2>
            <div className="fb-card" style={{ marginBottom: '2rem' }}>
              <div className="fb-subcard">
                <label className="fb-label">
                  Calendar Link (Optional)
                </label>
                <p className="fb-muted" style={{ fontSize: '0.9rem', marginBottom: '1rem' }}>
                  Calendly, Google Calendar, Cal.com - any scheduling tool
                </p>
                <input
                  type="url"
                  placeholder="https://calendly.com/yourlink"
                  value={aiCalendarLink}
                  onChange={(e) => setAiCalendarLink(e.target.value)}
                  className="fb-input"
                />
              </div>

              {/* Delay Configuration */}
              <div className="fb-subcard-compact">
                <label className="fb-label">
                  Reply Delay (Make it look natural)
                </label>
                <p className="fb-muted" style={{ fontSize: '0.85rem', marginBottom: '0.75rem' }}>
                  Add a delay between emails to make replies look natural (not instant AI). For testing, set to 0 for instant replies.
                </p>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  <div style={{ flex: '1', minWidth: '150px' }}>
                    <label className="fb-label" style={{ fontSize: '0.85rem' }}>
                      Min Delay (minutes)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="60"
                      value={formSettings.aiReplyDelayMin || 0}
                      onChange={(e) => {
                        const min = parseInt(e.target.value) || 0;
                        const max = Math.max(min, formSettings.aiReplyDelayMax || 0);
                        setFormSettings({ 
                          ...formSettings, 
                          aiReplyDelayMin: min,
                          aiReplyDelayMax: max
                        });
                      }}
                      className="fb-input"
                    />
                  </div>
                  <div style={{ flex: '1', minWidth: '150px' }}>
                    <label className="fb-label" style={{ fontSize: '0.85rem' }}>
                      Max Delay (minutes)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="60"
                      value={formSettings.aiReplyDelayMax || 0}
                      onChange={(e) => {
                        const max = parseInt(e.target.value) || 0;
                        const min = Math.min(max, formSettings.aiReplyDelayMin || 0);
                        setFormSettings({ 
                          ...formSettings, 
                          aiReplyDelayMax: max,
                          aiReplyDelayMin: min
                        });
                      }}
                      className="fb-input"
                    />
                  </div>
                </div>
                <p className="fb-faint" style={{ fontSize: '0.75rem', marginTop: '0.5rem', fontStyle: 'italic' }}>
                  💡 AI will wait a random time between min and max before sending. Set both to 0 for instant (testing only).
                </p>
              </div>

              {/* Booking Handoff Setting */}
              <div className="fb-subcard-compact">
                <label className="fb-checkbox-row">
                  <input
                    type="checkbox"
                    checked={formSettings.pauseAiOnBooking !== false}
                    onChange={(e) => setFormSettings({ ...formSettings, pauseAiOnBooking: e.target.checked })}
                    style={{ width: '20px', height: '20px' }}
                  />
                  <span>Pause AI on Booking (Jump-In Handoff)</span>
                </label>
                <p className="fb-muted fb-indent-block" style={{ fontSize: '0.85rem' }}>
                  When a lead's reply confirms a booking, automatically pause the AI and send you a "jump in" email with a direct link to the thread. You can re-enable AI for the lead at any time.
                </p>
              </div>

              {/* Follow-Up Email Settings */}
              <div className="fb-subcard-compact">
                <label className="fb-checkbox-row" style={{ marginBottom: '0.75rem' }}>
                  <input
                    type="checkbox"
                    checked={formSettings.followUpEnabled !== false}
                    onChange={(e) => setFormSettings({ ...formSettings, followUpEnabled: e.target.checked })}
                    style={{ width: '20px', height: '20px' }}
                  />
                  <span>Follow-Up Emails</span>
                </label>
                <p className="fb-muted fb-indent-block" style={{ fontSize: '0.85rem', marginBottom: '0.75rem' }}>
                  Automatically send follow-up emails if leads don't reply. Includes a note to disregard if they've already booked.
                </p>
                
                {formSettings.followUpEnabled !== false && (
                  <div style={{ marginLeft: '28px', display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ flex: '1', minWidth: '150px' }}>
                      <label className="fb-label" style={{ fontSize: '0.85rem' }}>
                        Days Between Follow-Ups
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="30"
                        value={formSettings.followUpDelayDays || 2}
                        onChange={(e) => setFormSettings({ 
                          ...formSettings, 
                          followUpDelayDays: parseInt(e.target.value) || 2
                        })}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: '2px solid #e0e0e0',
                          borderRadius: '4px',
                          fontSize: '0.9rem',
                        }}
                      />
                    </div>
                    <div style={{ flex: '1', minWidth: '150px' }}>
                      <label className="fb-label" style={{ fontSize: '0.85rem' }}>
                        Max Follow-Ups
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="10"
                        value={formSettings.maxFollowUps || 2}
                        onChange={(e) => setFormSettings({ 
                          ...formSettings, 
                          maxFollowUps: parseInt(e.target.value) || 2
                        })}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: '2px solid #e0e0e0',
                          borderRadius: '4px',
                          fontSize: '0.9rem',
                        }}
                      />
                    </div>
                  </div>
                )}
                <p className="fb-faint fb-indent-block" style={{ fontSize: '0.75rem', marginTop: '0.5rem', fontStyle: 'italic' }}>
                  💡 Follow-up emails will include: "If you've already booked or scheduled a meeting, please disregard this follow-up email."
                </p>
              </div>

            </div>

            <h2 className="fb-section-title">3. Test & Turn on</h2>
            {formSettings.knowledgeBaseId && (
              <TestAIConversation
                testLeadName={testLeadName}
                setTestLeadName={setTestLeadName}
                testLeadEmail={testLeadEmail}
                setTestLeadEmail={setTestLeadEmail}
                testLeadMessage={testLeadMessage}
                setTestLeadMessage={setTestLeadMessage}
                conversationHistory={conversationHistory}
                setConversationHistory={setConversationHistory}
                setAiReplyPreview={setAiReplyPreview}
                testingAiReply={testingAiReply}
                onTest={testAIReply}
                onDownload={sendTestEmail}
                showDownload
              />
            )}

            {/* SEND TEST EMAIL TO INBOX */}
            {formSettings.knowledgeBaseId && (
              <div className="fb-card fb-card-success" style={{ marginBottom: '2rem' }}>
                <h3 className="fb-subsection-title" style={{ marginTop: 0 }}>Send Real Test Email</h3>
                <p className="fb-muted" style={{ marginBottom: '1.5rem' }}>
                  Send an actual AI-generated email to your inbox to preview what leads will receive. Uses your real knowledge base and calendar link.
                  <br />
                  <strong>Note:</strong> Test emails will appear in your Leads section marked as "🧪 TEST". 
                  <br />
                  <span style={{ fontSize: '0.85rem', color: '#28a745', fontWeight: '500' }}>
                    ✅ Replying to test emails will trigger AI responses! The conversation will continue automatically.
                  </span>
                </p>

                <div style={{ marginBottom: '1rem' }}>
                  <label className="fb-label">
                    Send test email to:
                  </label>
                  <input
                    type="email"
                    placeholder={currentUser?.email || 'your@email.com'}
                    value={testEmailRecipient}
                    onChange={(e) => setTestEmailRecipient(e.target.value)}
                    className="fb-input" style={{ marginBottom: '1rem' }}
                  />
                  
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', marginTop: '1rem' }}>
                    👤 Lead Name:
                  </label>
                  <input
                    type="text"
                    placeholder="John Doe"
                    value={testEmailName}
                    onChange={(e) => setTestEmailName(e.target.value)}
                    className="fb-input" style={{ marginBottom: '1rem' }}
                  />
                  
                  <label className="fb-label">
                    💬 Lead Message:
                  </label>
                  <textarea
                    placeholder="I am interested in your services and would like to learn more..."
                    value={testEmailMessage}
                    onChange={(e) => setTestEmailMessage(e.target.value)}
                    rows={4}
                    className="fb-textarea" style={{ marginBottom: '1rem' }}
                  />
                  
                  <button
                    type="button"
                    className="inbox-pill-btn-primary"
                    onClick={sendRealTestEmail}
                    disabled={sendingTestEmail}
                    style={{ width: '100%', padding: '1rem', fontSize: '1.1rem', opacity: sendingTestEmail ? 0.6 : 1, cursor: sendingTestEmail ? 'not-allowed' : 'pointer' }}
                  >
                    {sendingTestEmail ? 'Sending...' : 'Send Test Email to Inbox'}
                  </button>
                </div>

                <div className="fb-callout info" style={{ fontSize: '0.85rem' }}>
                  <strong>What happens:</strong>
                  <ol style={{ marginTop: '0.5rem', marginBottom: 0, paddingLeft: '1.5rem' }}>
                    <li>Creates test submission</li>
                    <li>AI generates reply using your KB</li>
                    <li>Email sent via Postmark</li>
                    <li>Arrives in 30-60 seconds</li>
                  </ol>
                </div>
              </div>
            )}

            {/* Phase 1c/A2P approval un-gates the builder toggle (FORM_SMS_OPT_IN). */}
            {showSmsOptInBuilder && !workspaceSmsA2pApproved && (
              <div className="fb-card" style={{ marginBottom: '2rem' }}>
                <h3 className="fb-subsection-title" style={{ marginTop: 0 }}>SMS opt-in</h3>
                <p className="fb-muted" style={{ fontSize: '0.85rem', marginBottom: '1rem' }}>
                  Capture express written consent for SMS. Get a dedicated number on Text Messaging first.
                </p>
                <button
                  type="button"
                  className="inbox-pill-btn-primary"
                  onClick={() => navigate(formId ? `/text-messaging?formId=${formId}` : '/text-messaging')}
                >
                  Add texting to this form
                </button>
              </div>
            )}
            {showSmsOptInBuilder && workspaceSmsA2pApproved && (
              <div className="fb-card" style={{ marginBottom: '2rem' }}>
                <h3 className="fb-subsection-title" style={{ marginTop: 0 }}>SMS opt-in</h3>
                <p className="fb-muted" style={{ fontSize: '0.85rem', marginBottom: '1rem' }}>
                  Capture express written consent for SMS. Sending is live for this workspace — enable opt-in on forms that collect a phone number.
                </p>
                <label className="fb-checkbox-row" style={{ gap: '0.75rem', marginBottom: '1rem' }}>
                  <input
                    type="checkbox"
                    checked={!!formSettings.smsOptIn?.enabled}
                    onChange={(e) => {
                      const enabled = e.target.checked;
                      const businessName = resolveFromName({
                        formBusinessName: formSettings.businessName,
                        workspaceBusinessName: workspaceBrand?.businessName,
                        workspaceName: workspaceBrand?.name,
                      });
                      setFormSettings({
                        ...formSettings,
                        smsOptIn: enabled
                          ? {
                              enabled: true,
                              consentText: formSettings.smsOptIn?.consentText || buildConsentText({
                                businessName,
                                formName,
                              }),
                            }
                          : { enabled: false, consentText: formSettings.smsOptIn?.consentText || DEFAULT_SMS_CONSENT_TEXT },
                      });
                    }}
                    style={{ width: '20px', height: '20px' }}
                  />
                  <span style={{ fontSize: '0.95rem', fontWeight: '500' }}>
                    Enable SMS opt-in on this form
                  </span>
                </label>
                {formSettings.smsOptIn?.enabled && (
                  <>
                    {!fields.some((f) => f.type === 'phone') && (
                      <div className="fb-callout warn" style={{ fontSize: '0.85rem', marginBottom: '1rem' }}>
                        This form needs a phone field for SMS opt-in.{' '}
                        <button
                          type="button"
                          className="fb-link-btn"
                          onClick={() => {
                            onAddPhoneField();
                            showToast('Phone field added — save the form to apply.', 'success');
                          }}
                        >
                          Add phone field
                        </button>
                      </div>
                    )}
                    <label className="fb-label">Consent text (shown beside the checkbox)</label>
                    <textarea
                      className="fb-input fb-input-wide"
                      rows={5}
                      value={formSettings.smsOptIn?.consentText || ''}
                      onChange={(e) =>
                        setFormSettings({
                          ...formSettings,
                          smsOptIn: {
                            ...formSettings.smsOptIn,
                            enabled: true,
                            consentText: e.target.value,
                          },
                        })
                      }
                      style={{ marginBottom: '0.75rem' }}
                    />
                    {smsConsentEdited && (
                      <div className="fb-callout warn" style={{ fontSize: '0.85rem', marginBottom: '0.75rem' }}>
                        Your workspace has an approved A2P campaign tied to the current consent text.
                        Editing this copy may require a campaign resubmit before SMS can stay compliant.
                      </div>
                    )}
                    <p className="fb-muted" style={{ fontSize: '0.85rem', margin: 0 }}>
                      The consent checkbox is always optional. Opting in must not be required to submit the form.
                    </p>
                  </>
                )}
              </div>
            )}

            <div className="fb-card" style={{ marginBottom: '2rem' }}>
              <div className="fb-subcard-compact">
                <label className="fb-checkbox-row" style={{ gap: '0.75rem' }}>
                  <input
                    type="checkbox"
                    checked={aiReplyEnabled}
                    onChange={(e) => setAiReplyEnabled(e.target.checked)}
                    style={{ width: '20px', height: '20px' }}
                  />
                  <span style={{ fontSize: '0.95rem', fontWeight: '500' }}>
                    Enable AI replies to real leads (save form to apply)
                  </span>
                </label>
                {formSettings.knowledgeBaseId && aiReplyEnabled && (
                  <p style={{ fontSize: '0.85rem', marginTop: '0.5rem', marginBottom: 0, opacity: 0.95 }}>
                    ✅ AI replies are ON for this form. Save form after any change to keep settings.
                  </p>
                )}
                {aiReplyEnabled && !formSettings.knowledgeBaseId && (
                  <p className="fb-callout warn" style={{ fontSize: '0.85rem', marginTop: '0.5rem', marginBottom: 0 }}>
                    ⚠ Pick a knowledge base. AI replies need one to answer from. Set it up in the Knowledge tab. Form won't save with AI reply on until a KB is linked.
                  </p>
                )}
              </div>

              <p className="fb-muted" style={{ fontSize: '0.85rem', marginTop: '1rem', textAlign: 'center' }}>
                Emails sent from:{' '}
                <strong>
                  {senderMode === 'domain' && availableDomains.find((d) => d.id === selectedDomainId)?.verificationStatus === 'verified'
                    ? availableDomains.find((d) => d.id === selectedDomainId).fromEmail
                    : 'replies@gocta.ai'}
                </strong>
              </p>

            </div>
            </div>
            )}
          </div>
  );
}
